import DeleteIcon from '@mui/icons-material/Delete'
import { Box, Button, IconButton } from '@mui/material'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'

import { Modal } from '../../../../components'
import { useOptions } from '../../../../providers/Options/Options.provider'
import { usePeerGroups } from '../../../../providers/PeerGroups/PeerGroups.provider'
import {
  PeerGroupProvider,
  PeerGroupSession,
} from '../../../../providers/PeerGroups/types'
import { themeColors } from '../../../../theme'

interface AddSessionProvidersModalProps {
  setShowAddSessionProvidersModal: React.Dispatch<React.SetStateAction<boolean>>
  showAddSessionProvidersModal: boolean
}

export const AddSessionProvidersModal = ({
  showAddSessionProvidersModal,
  setShowAddSessionProvidersModal,
}: AddSessionProvidersModalProps) => {
  const {
    peerGroupDetails,
    addRemoveProvidersSinglePeerGroupSession,
    getPeerGroupDetails,
  } = usePeerGroups()

  const [selectedSessionId, setSelectedSessionId] = useState('')
  const [selectedSession, setSelectedSession] = useState<PeerGroupSession>({
    id: 0,
    start: '',
    end: '',
    mediaIds: [],
    moderatorObservers: [],
  })

  const [selectedProviders, setSelectedProviders] = useState<
    PeerGroupProvider[]
  >([])

  const [selectedProviderId, setSelectedProviderId] = useState<number>(0)

  const { options } = useOptions()

  const handleAddProvider = (providerId: number) => {
    setSelectedProviders((prevPeerGroupProviders: PeerGroupProvider[]) => {
      if (prevPeerGroupProviders.every((p) => p.providerId !== providerId)) {
        const providerOption = options.providers.find(
          (p) => p.providerId === providerId
        )
        const name = providerOption.label.replace(/\s*\(.*?\)/, '').trim()
        const newPGProvider = {
          providerId,
          isModerator: false,
          isCoModerator: false,
          isObserver: false,
          displayName: name,
        }
        return [...prevPeerGroupProviders, newPGProvider]
      } else {
        return [...prevPeerGroupProviders]
      }
    })
  }

  const handleRemoveProvider = (providerId: number) => {
    setSelectedProviders((prevProviders) => {
      return prevProviders.filter((p) => p.providerId !== providerId)
    })
  }

  const handleRoleRadioChange = (
    providerId: number,
    roleProperty: string,
    checked: boolean
  ) => {
    setSelectedProviders((prevProviders) =>
      prevProviders.map((provider) => {
        if (provider.providerId !== providerId) return provider

        return {
          ...provider,
          isModerator: false,
          isCoModerator: false,
          isObserver: false,
          [roleProperty]: checked,
        }
      })
    )
  }

  useEffect(() => {
    if (
      peerGroupDetails &&
      peerGroupDetails.sessions &&
      peerGroupDetails.sessions.length > 0
    ) {
      setSelectedSessionId(String(peerGroupDetails?.sessions[0].id))
    }
  }, [peerGroupDetails])

  useEffect(() => {
    if (options.providers !== null) {
      setSelectedProviderId(options.providers[0].providerId)
    }
  }, [options.providers])

  useEffect(() => {
    const matchingSession = peerGroupDetails?.sessions?.find(
      (session) => session.id === Number(selectedSessionId)
    )
    if (matchingSession) {
      setSelectedSession(matchingSession)
    }
  }, [selectedSessionId, peerGroupDetails])

  useEffect(() => {
    setSelectedProviders(selectedSession.moderatorObservers ?? [])
  }, [selectedSession])

  const handleSave = async () => {
    await addRemoveProvidersSinglePeerGroupSession({
      sessionId: parseInt(selectedSessionId),
      moderatorObservers: selectedProviders,
    })
    await getPeerGroupDetails(peerGroupDetails?.peerGroupId)
    setShowAddSessionProvidersModal(false)
  }

  return (
    <Modal
      open={showAddSessionProvidersModal}
      setOpen={setShowAddSessionProvidersModal}
      title={'ADD MODERATORS, CO-MODERATORS, & OBSERVERS TO SESSIONS'}
      width='clamp(320px, 92vw, 800px)'
      ModalContent={
        <Box
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'space-between',
            justifyContent: 'space-between',
          }}
          paddingX={2}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: 1.5,
              width: '100%',
              mb: 2,
            }}
          >
            <Box component='h3' sx={{ m: 0, whiteSpace: 'nowrap' }}>
              Session Date:
            </Box>
            <Box
              component='select'
              onChange={(e) => setSelectedSessionId(e.target.value)}
              sx={{ flex: 1, minWidth: 0, p: 1 }}
            >
              {peerGroupDetails?.sessions?.map((session) => {
                return (
                  <option key={session.id} value={session.id}>
                    {dayjs(session.start).format('MM/DD/YYYY hh:mmA')}
                  </option>
                )
              })}
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: 1.5,
              width: '100%',
              mb: 2,
            }}
          >
            <Box component='h3' sx={{ m: 0, whiteSpace: 'nowrap' }}>
              Providers:
            </Box>

            <Box
              component='select'
              onChange={(e) => setSelectedProviderId(parseInt(e.target.value))}
              sx={{ flex: 1, minWidth: 0, p: 1 }}
            >
              {options.providers.map((provider) => {
                return (
                  <option key={provider.providerId} value={provider.providerId}>
                    {provider.label}
                  </option>
                )
              })}
            </Box>

            <Button
              variant='contained'
              onClick={() => {
                handleAddProvider(selectedProviderId)
              }}
              style={{ width: '2px' }}
            >
              Add
            </Button>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              maxHeight: { xs: '45vh', sm: 300 },
              overflowY: 'auto',
              p: 1.5,
            }}
          >
            {selectedProviders.map((provider, i) => {
              const currentRole = provider.isModerator
                ? 'isModerator'
                : provider.isCoModerator
                ? 'isCoModerator'
                : provider.isObserver
                ? 'isObserver'
                : null

              return (
                <Box
                  key={`peer-group-provider-${i}`}
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    backgroundColor: '#f9fafb',
                    border: '1px solid #e5e7eb',
                    maxWidth: '700px',
                    width: '100%',
                    alignSelf: 'center',
                  }}
                >
                  <span
                    style={{
                      fontSize: '14px',
                      fontWeight: 500,
                      flex: 1,
                      marginRight: '16px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {provider.displayName}
                  </span>

                  {/* Role radio button group */}
                  <div
                    style={{
                      display: 'flex',
                      gap: '16px',
                      alignItems: 'center',
                    }}
                  >
                    {[
                      { key: 'isModerator', label: 'Moderator' },
                      { key: 'isCoModerator', label: 'Co-Moderator' },
                      { key: 'isObserver', label: 'Observer' },
                    ].map((role) => (
                      <label
                        key={role.key}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          cursor: 'pointer',
                          fontSize: '13px',
                          color:
                            currentRole === role.key
                              ? themeColors.blue
                              : themeColors.gray,
                          fontWeight: currentRole === role.key ? 500 : 400,
                        }}
                      >
                        <input
                          type='radio'
                          name={`role-${provider.providerId}`}
                          checked={currentRole === role.key}
                          onChange={() => {
                            handleRoleRadioChange(
                              provider.providerId,
                              role.key,
                              true
                            )
                          }}
                          style={{
                            width: '16px',
                            height: '16px',
                            accentColor: themeColors.blue,
                            cursor: 'pointer',
                          }}
                        />
                        {role.label}
                      </label>
                    ))}
                  </div>

                  {/* Delete button */}
                  <IconButton
                    color='error'
                    aria-label='delete'
                    onClick={() => handleRemoveProvider(provider.providerId)}
                    style={{ marginLeft: '16px' }}
                    size='small'
                  >
                    <DeleteIcon fontSize='small' />
                  </IconButton>
                </Box>
              )
            })}
          </Box>
          <Button
            variant='contained'
            onClick={() => {
              handleSave()
            }}
            style={{ width: '50px', alignSelf: 'center', marginTop: '10px' }}
          >
            Save
          </Button>
        </Box>
      }
    />
  )
}
